import React, { useState, useEffect, useRef } from 'react';
import Board from './components/Board';
import { loadKnowledgebase, saveKnowledgebase, deleteKnowledgebase, loadSettings, saveSettings } from './utils/firebaseSettings';
import PricingModal from './components/PricingModal';
import SubscriptionModal from './components/SubscriptionModal';
import OnboardingModal from './components/OnboardingModal';
import StripeSettings from './pages/StripeSettings';
import { checkSubscriptionStatus } from './utils/stripe';
import logo from './images/logo.png';

const HomePage = ({ emails, setEmails, accessToken, userInfo, isNewLogin }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pricingOpen, setPricingOpen] = useState(false);
  const [subscriptionOpen, setSubscriptionOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [stripeSettingsOpen, setStripeSettingsOpen] = useState(false);
  const [knowledgebase, setKnowledgebase] = useState(null);
  const [kbLoading, setKbLoading] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [subscriptionTrialEnd, setSubscriptionTrialEnd] = useState(null);
  const [isOnTrial, setIsOnTrial] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(null);
  const [currentPeriodStart, setCurrentPeriodStart] = useState(null);
  const [userSettings, setUserSettings] = useState({
    signOffName: '',
    kbLink: '',
    personality: 'professional',
    hasSeenOnboarding: false
  });
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check for successful Stripe checkout
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId && userInfo) {
      // User returned from successful Stripe checkout
      console.log('✅ Stripe checkout successful! Session ID:', sessionId);

      // Show success message and reload
      alert('🎉 Welcome to Inbox Mate Pro! Your subscription is now active.');

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);

      // Reload to fetch subscription from Stripe
      window.location.reload();
    }
  }, [userInfo]);

  // Note: Onboarding logic is handled in loadData useEffect
  // We check isNewLogin AND hasSeenOnboarding to determine if we should show onboarding

  // Load knowledgebase, settings, and check subscription on mount
  useEffect(() => {
    const loadData = async () => {
      if (userInfo) {
        setKbLoading(true);

        // Load knowledge base from Firebase (using email instead of accessToken)
        const content = await loadKnowledgebase(userInfo.email);
        setKnowledgebase(content);

        // Load settings from Firebase (using email instead of accessToken)
        const settings = await loadSettings(userInfo.email);
        setUserSettings({
          signOffName: settings.signOffName || '',
          kbLink: settings.kbLink || '',
          personality: settings.personality || 'professional',
          hasSeenOnboarding: settings.hasSeenOnboarding || false
        });

        // Show onboarding ONLY if user hasn't seen it before (regardless of isNewLogin)
        // This ensures returning users never see onboarding again
        if (!settings.hasSeenOnboarding) {
          console.log('User has not seen onboarding, showing it now');
          setOnboardingOpen(true);
        } else {
          console.log('User has already seen onboarding, skipping');
        }

        // Check subscription status directly from Stripe
        const subscriptionData = await checkSubscriptionStatus(userInfo.email);
        console.log('📊 Subscription data from Stripe:', subscriptionData);
        setHasSubscription(subscriptionData.hasSubscription);
        setSubscriptionPlan(subscriptionData.plan);
        setSubscriptionTrialEnd(subscriptionData.trialEnd);
        setIsOnTrial(subscriptionData.isOnTrial);
        setCancelAtPeriodEnd(subscriptionData.cancelAtPeriodEnd);
        setCurrentPeriodEnd(subscriptionData.currentPeriodEnd);
        setCurrentPeriodStart(subscriptionData.currentPeriodStart);
        console.log('✅ Set currentPeriodEnd to:', subscriptionData.currentPeriodEnd);
        console.log('✅ Set currentPeriodStart to:', subscriptionData.currentPeriodStart);

        // If user has no subscription and has seen onboarding, show pricing after load
        if (!subscriptionData.hasSubscription && settings.hasSeenOnboarding) {
          setPricingOpen(true);
        }

        setKbLoading(false);
      }
    };
    loadData();
  }, [userInfo, isNewLogin]);

  // Save settings whenever they change (using Firebase)
  const updateSettings = async (newSettings) => {
    setUserSettings(newSettings);
    if (userInfo) {
      await saveSettings(newSettings, userInfo.email);
    }
  };

  // Handle onboarding completion
  const handleOnboardingClose = async () => {
    // Save onboarding completion to Firebase settings
    const updatedSettings = { ...userSettings, hasSeenOnboarding: true };
    console.log('Saving hasSeenOnboarding = true to Firebase');
    await updateSettings(updatedSettings);
    setOnboardingOpen(false);
    console.log('Onboarding closed, hasSeenOnboarding saved');
    // Show pricing modal after onboarding
    setPricingOpen(true);
  };

  // Handle settings update from onboarding
  const handleOnboardingSettingsUpdate = (settings, knowledgebaseContent) => {
    setUserSettings(settings);
    if (knowledgebaseContent) {
      setKnowledgebase(knowledgebaseContent);
    }
  };

  return (
    <div className="flex flex-col h-screen">
      {/* Header */}
      <header className="bg-white px-4 py-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img src={logo} alt="Inbox Mate" className="w-10 h-10 object-contain" />
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Inbox Mate</h1>
        </div>

        <div className="flex items-center gap-4">
          {/* Subscription Badge */}
          {hasSubscription && subscriptionPlan && (
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                subscriptionPlan === 'pro'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-600 text-white'
              }`}>
                {subscriptionPlan === 'pro' ? 'PRO' : 'BASIC'}
              </span>
              {cancelAtPeriodEnd && currentPeriodEnd ? (
                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                  Cancels: {Math.max(0, Math.ceil((currentPeriodEnd - Date.now()) / (1000 * 60 * 60 * 24)))}d
                </span>
              ) : isOnTrial && subscriptionTrialEnd ? (
                <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                  Trial: {Math.max(0, Math.ceil((subscriptionTrialEnd - Date.now()) / (1000 * 60 * 60 * 24)))}d left
                </span>
              ) : null}
            </div>
          )}

          {/* User Dropdown */}
          <div className="relative" ref={dropdownRef}>
            {userInfo && (
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-2 hover:bg-gray-50 p-2 rounded-lg transition"
              >
                <img
                  src={userInfo.picture}
                  alt={userInfo.name}
                  className="w-10 h-10 rounded-full"
                />
                <div className="text-left">
                  <div className="text-sm font-bold text-gray-900">{userInfo.name}</div>
                  <div className="text-xs text-gray-500">{userInfo.email}</div>
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 text-gray-500 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}

            {/* Dropdown menu */}
            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
              <button
                onClick={() => {
                  setSettingsOpen(true);
                  setDropdownOpen(false);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Settings
              </button>
              <button
                onClick={() => {
                  if (hasSubscription) {
                    // If subscribed, open Subscription modal
                    setSubscriptionOpen(true);
                  } else {
                    // If not subscribed, open Pricing modal
                    setPricingOpen(true);
                  }
                  setDropdownOpen(false);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {hasSubscription ? 'Manage Subscription' : 'Upgrade to Pro'}
              </button>
              <button
                onClick={() => {
                  setStripeSettingsOpen(true);
                  setDropdownOpen(false);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
                Connect Stripe Account
              </button>
              <button
                onClick={() => {
                  // Preserve knowledge base and link when logging out
                  const kb = localStorage.getItem('knowledgebase');
                  const kbLink = localStorage.getItem('knowledgebase-link');

                  localStorage.clear();
                  sessionStorage.clear();

                  // Restore knowledge base and link
                  if (kb) localStorage.setItem('knowledgebase', kb);
                  if (kbLink) localStorage.setItem('knowledgebase-link', kbLink);

                  document.location.href = '/';
                }}
                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Settings Modal */}
      {settingsOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900">Settings</h2>
              <button
                onClick={() => setSettingsOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all shadow-sm hover:shadow-md border border-gray-200 hover:border-gray-300"
                title="Close"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Knowledgebase Section */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">AI Knowledgebase</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload a knowledgebase file (markdown or text) to help AI generate better responses. This will be saved to your Google Drive.
                  </p>

                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                    <input
                      type="file"
                      id="knowledgebase-upload"
                      accept=".md,.txt"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onload = async (event) => {
                            const content = event.target.result;
                            setKbLoading(true);
                            try {
                              const result = await saveKnowledgebase(content, userInfo.email);
                              if (result.success) {
                                setKnowledgebase(content);
                                alert('✅ Knowledgebase uploaded successfully to Firebase!');
                              } else {
                                alert('❌ Error uploading knowledgebase: ' + result.error);
                              }
                            } catch (error) {
                              alert('❌ Error uploading knowledgebase');
                              console.error(error);
                            }
                            setKbLoading(false);
                          };
                          reader.readAsText(file);
                        }
                      }}
                    />
                    <label
                      htmlFor="knowledgebase-upload"
                      className="cursor-pointer inline-flex flex-col items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <span className="text-sm font-medium text-gray-700 mb-1">
                        Click to upload knowledgebase
                      </span>
                      <span className="text-xs text-gray-500">
                        Supports .md and .txt files
                      </span>
                    </label>
                  </div>

                  {/* Loading state */}
                  {kbLoading && (
                    <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm text-blue-700">Syncing with Firebase...</span>
                      </div>
                    </div>
                  )}

                  {/* Current knowledgebase preview */}
                  {!kbLoading && knowledgebase && (
                    <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center gap-2">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-sm font-medium text-green-900">Knowledgebase Loaded from Firebase</span>
                        </div>
                        <button
                          onClick={async () => {
                            if (window.confirm('Are you sure you want to remove the knowledgebase from Firebase?')) {
                              setKbLoading(true);
                              const result = await deleteKnowledgebase(userInfo.email);
                              if (result.success) {
                                setKnowledgebase(null);
                                alert('✅ Knowledgebase removed successfully');
                              } else {
                                alert('❌ Error removing knowledgebase: ' + result.error);
                              }
                              setKbLoading(false);
                            }
                          }}
                          className="text-xs text-red-600 hover:text-red-800"
                        >
                          Remove
                        </button>
                      </div>
                      <div className="text-xs text-gray-700 font-mono bg-white p-3 rounded border border-green-300 max-h-32 overflow-y-auto">
                        {knowledgebase.substring(0, 300)}...
                      </div>
                    </div>
                  )}

                  {/* No knowledgebase message */}
                  {!kbLoading && !knowledgebase && (
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200 text-center">
                      <p className="text-sm text-gray-600">No knowledgebase uploaded yet</p>
                    </div>
                  )}

                  {/* User Settings */}
                  <div className="mt-6 pt-6 border-t border-gray-200 space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Email Response Settings</h3>
                      <p className="text-xs text-gray-500 mt-1">💾 Settings are automatically saved to your Google Drive account</p>
                    </div>

                    {/* Sign-off Name */}
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Sign-off Name
                      </label>
                      <p className="text-sm text-gray-600 mb-3">
                        Your name to use when signing off AI-generated emails
                      </p>
                      <input
                        type="text"
                        value={userSettings.signOffName}
                        onChange={(e) => {
                          updateSettings({ ...userSettings, signOffName: e.target.value });
                        }}
                        placeholder="e.g., Sarah, Support Team, etc."
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>

                    {/* Personality */}
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Response Personality
                      </label>
                      <p className="text-sm text-gray-600 mb-3">
                        Choose the tone and style for AI-generated responses
                      </p>
                      <select
                        value={userSettings.personality}
                        onChange={(e) => {
                          updateSettings({ ...userSettings, personality: e.target.value });
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                      >
                        <option value="professional">🎯 Professional - Clear, direct, and business-focused</option>
                        <option value="friendly">😊 Friendly - Warm, conversational, and approachable</option>
                        <option value="empathetic">💙 Empathetic - Compassionate, understanding, and supportive</option>
                        <option value="enthusiastic">✨ Enthusiastic - Energetic, positive, and encouraging</option>
                        <option value="concise">⚡ Concise - Brief, to-the-point, and efficient</option>
                      </select>
                    </div>

                    {/* Knowledge Base Link */}
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">
                        Public Support Documentation Link
                      </label>
                      <p className="text-sm text-gray-600 mb-3">
                        Link to your publicly available help center, docs, or FAQ that will be included in AI-generated email responses
                      </p>
                      <input
                        type="url"
                        value={userSettings.kbLink}
                        onChange={(e) => {
                          updateSettings({ ...userSettings, kbLink: e.target.value });
                        }}
                        placeholder="https://help.yourcompany.com or https://docs.yourcompany.com"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>

                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setSettingsOpen(false)}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Modal */}
      <PricingModal
        isOpen={pricingOpen}
        onClose={() => setPricingOpen(false)}
        userEmail={userInfo?.email}
        requireSubscription={!hasSubscription}
      />

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={subscriptionOpen}
        onClose={() => setSubscriptionOpen(false)}
        userInfo={userInfo}
        subscriptionPlan={subscriptionPlan}
        trialEnd={subscriptionTrialEnd}
        isOnTrial={isOnTrial}
        cancelAtPeriodEnd={cancelAtPeriodEnd}
        currentPeriodEnd={currentPeriodEnd}
        currentPeriodStart={currentPeriodStart}
      />

      {/* Onboarding Modal */}
      <OnboardingModal
        isOpen={onboardingOpen}
        onClose={handleOnboardingClose}
        userEmail={userInfo?.email}
        onSettingsUpdate={handleOnboardingSettingsUpdate}
      />

      {/* Stripe Settings Modal */}
      {stripeSettingsOpen && (
        <StripeSettings
          userEmail={userInfo?.email}
          onClose={() => setStripeSettingsOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 overflow-hidden">
        <Board
          emails={emails}
          setEmails={setEmails}
          accessToken={accessToken}
          knowledgebase={knowledgebase}
          userSettings={userSettings}
          subscriptionPlan={subscriptionPlan}
          userInfo={userInfo}
          currentPeriodStart={currentPeriodStart}
          currentPeriodEnd={currentPeriodEnd}
        />
      </main>
    </div>
  );
};

export default HomePage;
